import { useState, useEffect } from 'react';

/**
 * Hook personalizado para persistir y recuperar datos de formulario en localStorage
 * 
 * @param {string} storageKey - Clave para guardar en localStorage
 * @param {Object} initialFormData - Estado inicial del formulario
 * @param {number} autoSaveInterval - Intervalo en ms para guardar automáticamente
 * @returns {[Object, Function, Function]} - [formData, setPersistedFormData, clearFormData]
 */
export const useFormPersistence = (storageKey, initialFormData, autoSaveInterval = 5000) => {
  // Función para obtener datos guardados o usar initialFormData
  const getSavedFormData = () => {
    try {
      const savedData = localStorage.getItem(storageKey);
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        
        // Excluir imágenes al recuperar datos
        const { imagenes, ...formDataWithoutImages } = parsedData;
        
        // Combinar con initialFormData para asegurar que tengamos todos los campos
        return { ...initialFormData, ...formDataWithoutImages };
      }
      return initialFormData;
    } catch (error) {
      console.error('Error al recuperar datos del formulario:', error);
      return initialFormData;
    }
  };

  // Estado del formulario con datos guardados o iniciales
  const [formData, setFormData] = useState(getSavedFormData);
  
  // Bandera para controlar la persistencia automática
  const [shouldPersist, setShouldPersist] = useState(true);
  
  // Función para guardar en localStorage excluyendo las imágenes
  const persistFormData = (data) => {
    if (!shouldPersist) return;
    
    try {
      // Excluir imágenes y agregar timestamp
      const { imagenes, ...dataToSave } = data;
      
      // Agregar timestamp para saber cuándo se guardó
      const dataWithTimestamp = {
        ...dataToSave,
        _lastSaved: new Date().toISOString()
      };
      
      localStorage.setItem(storageKey, JSON.stringify(dataWithTimestamp));
    } catch (error) {
      console.error('Error al guardar datos del formulario:', error);
    }
  };
  
  // Función para actualizar el estado y guardar
  const setPersistedFormData = (updater) => {
    setFormData(prevData => {
      // Determinar los nuevos datos
      const newData = typeof updater === 'function'
        ? updater(prevData)
        : { ...prevData, ...updater };
      
      // Persistir los nuevos datos
      persistFormData(newData);
      
      return newData;
    });
  };
  
  // Función para limpiar datos guardados
  const clearFormData = () => {
    localStorage.removeItem(storageKey);
    setFormData(initialFormData);
  };
  
  // Guardar automáticamente en intervalos
  useEffect(() => {
    if (!shouldPersist) return;
    
    const intervalId = setInterval(() => {
      persistFormData(formData);
    }, autoSaveInterval);
    
    return () => clearInterval(intervalId);
  }, [formData, autoSaveInterval, shouldPersist]);
  
  // Guardar cuando el componente se desmonte o la ventana se cierre
  useEffect(() => {
    const handleBeforeUnload = () => {
      persistFormData(formData);
    };
    
    if (shouldPersist) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }
    
    return () => {
      if (shouldPersist) {
        persistFormData(formData);
        window.removeEventListener('beforeunload', handleBeforeUnload);
      }
    };
  }, [formData, shouldPersist]);
  
  return [formData, setPersistedFormData, clearFormData];
};